import { useState } from 'react';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight';
import { faMagnifyingGlass } from '@fortawesome/pro-solid-svg-icons/faMagnifyingGlass';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Anchor, Autocomplete, Button, Group, Paper, Stack, Text } from '@mantine/core';
import { useIntl } from 'react-intl';

import { CommodityDetails } from './CommodityDetails';
import { CommodityHazmatInfo } from './CommodityHazmatInfo';
import { handlingUnitsStepMessages } from './handling-units-step-messages';
import styles from './WizardCommodityCard.module.css';
import { normalizeToMinorWeightUnit } from '../../../../utils/weight-utils';
import { WeightInput } from '../../../common';
import { Dropdown } from '../../../common/Dropdown';
import { FREIGHT_CLASS_OPTIONS } from '../../../freight-browser/components/freight-form/freight-class-options';

import type { FreightClass, LtlCommodity } from '../../../../types/ltl';
import type { Weight, WeightUnit } from '../../../../types/weight';

export type WizardCommodityCardProps = {
  commodity: LtlCommodity;
  index: number;
  /** The seller's preferred display weight unit, used as the fallback when the commodity's own weightUnit is missing. */
  displayWeightUnit: WeightUnit;
  onChange: (next: LtlCommodity) => void;
  onRemove?: () => void;
  productSuggestions?: string[];
  onProductSearch?: (query: string) => void;
  onProductSelect?: (commodityId: string, productName: string) => void;
};

export const WizardCommodityCard = ({
  commodity,
  index,
  displayWeightUnit,
  onChange,
  onRemove,
  productSuggestions,
  onProductSearch,
  onProductSelect,
}: WizardCommodityCardProps) => {
  const intl = useIntl();

  const [isExpanded, setIsExpanded] = useState(true);
  const [areDetailsShown, setAreDetailsShown] = useState(false);
  const [isHazmatShown, setIsHazmatShown] = useState(commodity.isHazmat);

  // WeightInput's internal combine math assumes the passed-in unit is a minor/base unit
  // (Ounces/Grams), so always normalize into that family before handing it off.
  const originalWeightUnit = commodity.weightUnit ?? displayWeightUnit;
  const weightValue: Weight = normalizeToMinorWeightUnit({ value: commodity.weight, unit: originalWeightUnit });

  const handleProductNameChange = (value: string) => {
    onChange({ ...commodity, productName: value });
    onProductSearch?.(value);
  };

  const handleProductNameSubmit = (value: string) => {
    onChange({ ...commodity, productName: value });
    onProductSelect?.(commodity.id, value);
    // TODO(product autofill): populate commodity fields from selected product once Product type/endpoint is defined
  };

  const handleWeightChange = (next: Weight) => onChange({ ...commodity, weight: next.value, weightUnit: next.unit });

  const handleFreightClassChange = (value: string | null) => onChange({ ...commodity, freightClass: (value as FreightClass) ?? '' });

  return (
    <Paper bg='gray.0' data-testid={`commodity-card-${index}`} p='sm' shadow='none' withBorder>
      <Stack gap='sm'>
        <Group gap='xs' justify='space-between'>
          <Anchor className={styles.commodityHeader} component='button' onClick={() => setIsExpanded((prev) => !prev)} type='button'>
            <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} size='sm' />
            <Text fw={500} size='sm'>
              {intl.formatMessage(handlingUnitsStepMessages.commodityTitle, { number: index + 1 })}
            </Text>
          </Anchor>
          {onRemove ? (
            <Button color='red' data-testid={`remove-commodity-${index}`} onClick={onRemove} size='xs' variant='ghost'>
              {intl.formatMessage(handlingUnitsStepMessages.removeCommodity)}
            </Button>
          ) : null}
        </Group>

        {isExpanded && (
          <Stack gap='sm'>
            <Autocomplete
              data={productSuggestions ?? []}
              data-testid={`commodity-product-name-${index}`}
              label={intl.formatMessage(handlingUnitsStepMessages.productNameLabel)}
              onChange={handleProductNameChange}
              onOptionSubmit={handleProductNameSubmit}
              placeholder={intl.formatMessage(handlingUnitsStepMessages.productNamePlaceholder)}
              rightSection={<FontAwesomeIcon icon={faMagnifyingGlass} size='sm' />}
              value={commodity.productName ?? ''}
            />

            <Group align='flex-end' grow>
              <WeightInput onChange={handleWeightChange} showMinorUnit={false} value={weightValue} />
              <Dropdown
                data={FREIGHT_CLASS_OPTIONS}
                data-testid={`commodity-class-${index}`}
                label={intl.formatMessage(handlingUnitsStepMessages.classLabel)}
                onChange={handleFreightClassChange}
                placeholder={intl.formatMessage(handlingUnitsStepMessages.classPlaceholder)}
                value={commodity.freightClass || null}
              />
            </Group>

            <Anchor
              className={styles.detailsToggle}
              component='button'
              data-testid={`toggle-commodity-details-${index}`}
              onClick={() => setAreDetailsShown((prev) => !prev)}
              size='sm'
              type='button'
            >
              {intl.formatMessage(
                areDetailsShown ? handlingUnitsStepMessages.hideCommodityDetails : handlingUnitsStepMessages.showCommodityDetails,
              )}
            </Anchor>

            {areDetailsShown && <CommodityDetails commodity={commodity} index={index} onChange={onChange} />}

            <Anchor
              className={styles.detailsToggle}
              component='button'
              data-testid={`toggle-commodity-hazmat-${index}`}
              onClick={() => setIsHazmatShown((prev) => !prev)}
              size='sm'
              type='button'
            >
              {intl.formatMessage(isHazmatShown ? handlingUnitsStepMessages.hideHazmatInfo : handlingUnitsStepMessages.showHazmatInfo)}
            </Anchor>

            {isHazmatShown && <CommodityHazmatInfo commodity={commodity} index={index} onChange={onChange} />}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};
